import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { Link2, Plus, Trash2, ArrowRight } from 'lucide-react';

interface Port {
  id: string;
  name: string;
  port_type: string;
  rear_port_id: string | null;
}

interface PatchPanelMappingProps {
  equipmentId: string;
  equipmentName: string;
}

export function PatchPanelMapping({ equipmentId, equipmentName }: PatchPanelMappingProps) {
  const queryClient = useQueryClient();
  const [mappingDraft, setMappingDraft] = useState<{ frontId: string; rearId: string } | null>(null);

  const { data: ports = [], isLoading } = useQuery<Port[]>({
    queryKey: ['ports', equipmentId],
    queryFn: () => api.get(`/api/equipment/${equipmentId}/ports`).then(r => r.data),
  });

  const frontPorts = ports.filter(p => p.port_type === 'front' || (!p.rear_port_id && p.port_type !== 'rear'));
  const rearPorts = ports.filter(p => p.port_type === 'rear');

  const setMapping = useMutation({
    mutationFn: ({ portId, rearPortId }: { portId: string; rearPortId: string | null }) =>
      api.patch(`/api/equipment/ports/${portId}`, { rear_port_id: rearPortId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ports', equipmentId] });
      toast.success('Маппинг портов обновлён');
      setMappingDraft(null);
    },
    onError: () => toast.error('Ошибка обновления маппинга'),
  });

  const clearMapping = (portId: string) => {
    setMapping.mutate({ portId, rearPortId: null });
  };

  const applyMapping = () => {
    if (!mappingDraft) return;
    setMapping.mutate({ portId: mappingDraft.frontId, rearPortId: mappingDraft.rearId });
  };

  if (isLoading) {
    return <div className="text-slate-400 text-xs animate-pulse py-4">Загрузка портов...</div>;
  }

  if (rearPorts.length === 0) {
    return (
      <div className="text-slate-500 text-xs py-3 text-center border border-dashed border-slate-700 rounded-lg">
        Нет задних портов у <span className="text-slate-300 font-bold">{equipmentName}</span>.<br/>
        Добавьте порты с типом «rear» для настройки маппинга.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-3.5 h-3.5 text-purple-400" />
        <span className="text-xs font-bold text-slate-200 uppercase tracking-wider">
          Маппинг Front→Rear ({equipmentName})
        </span>
      </div>

      {/* Existing mappings */}
      <div className="space-y-1.5">
        {frontPorts.map(fp => {
          const mapped = fp.rear_port_id ? rearPorts.find(r => r.id === fp.rear_port_id) : null;
          return (
            <div key={fp.id} className={`flex items-center gap-2 p-2 rounded border text-[11px] font-mono ${
              mapped
                ? 'bg-purple-950/30 border-purple-700/60'
                : 'bg-slate-900 border-slate-800'
            }`}>
              <span className="text-cyan-300 font-bold w-24 truncate" title={fp.name}>{fp.name}</span>
              <ArrowRight className="w-3 h-3 text-slate-500 shrink-0" />
              {mapped ? (
                <>
                  <span className="text-purple-300 font-bold flex-1 truncate">{mapped.name}</span>
                  <button
                    onClick={() => clearMapping(fp.id)}
                    className="text-slate-500 hover:text-red-400 transition ml-auto shrink-0"
                    title="Удалить маппинг"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <span className="text-slate-600 flex-1 italic">не задан</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Add new mapping */}
      <div className="border-t border-slate-800 pt-3">
        <div className="text-[10px] text-slate-500 uppercase mb-2 font-bold">Новый маппинг</div>
        <div className="flex gap-2 items-center">
          <select
            className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
            value={mappingDraft?.frontId || ''}
            onChange={e => setMappingDraft(prev => ({ frontId: e.target.value, rearId: prev?.rearId || '' }))}
          >
            <option value="">Front порт...</option>
            {frontPorts.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />
          <select
            className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
            value={mappingDraft?.rearId || ''}
            onChange={e => setMappingDraft(prev => ({ frontId: prev?.frontId || '', rearId: e.target.value }))}
          >
            <option value="">Rear порт...</option>
            {rearPorts.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={applyMapping}
            disabled={!mappingDraft?.frontId || !mappingDraft?.rearId || setMapping.isPending}
            className="shrink-0 p-1.5 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title="Применить маппинг"
          >
            <Plus className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
